/**
 * The screen a person is looking at, as a CLOSED TYPED VALUE — and the one
 * function that turns it into the `context` field of a turn request.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE IS SO SMALL, AND WHY IT MUST STAY SMALL.
 *
 * A global assistant that knows what screen you are on is useful; a global
 * assistant that reads your screen is an exfiltration channel with a chat
 * bubble on it. The whole difference between the two is the size and shape of
 * the value this module is allowed to produce. So:
 *
 *   1. PAGE VISIBILITY IS NEVER AUTHORITY. Nothing here widens what may be
 *      read. The server re-authorizes every retrieval against the caller's own
 *      permissions regardless of what this value says — screen context is a
 *      HINT for tool selection and nothing else (the rule is stated at the top
 *      of `apps/api/src/domains/intelligence/context.ts`, enforced by the tool
 *      executor, and it is why `roles`/`scope` on that wire contract are
 *      documented as informational). A context naming a worker the caller
 *      cannot read produces a refusal, not a leak.
 *
 *   2. IDS AND KINDS ONLY. Every field this module can emit is either a fixed
 *      literal chosen by the kind, a UUID, an ISO date, or a token from a
 *      closed preset list. There is no field a display name, a client name, a
 *      diagnosis, an address or a free-text filter can travel in — not because
 *      such values are filtered, but because no code path here can construct
 *      one. `toWireContext` writes literals; it never copies an object through.
 *
 *   3. NOTHING IS READ OFF THE DOM. This module never touches `document`,
 *      `window`, a ref, or rendered text. Its only inputs are the router's
 *      pathname and a small allowlist of URL search keys, each validated by
 *      shape before it is kept.
 *
 *   4. AN UNRECOGNIZED KIND IS DROPPED, NOT FORWARDED. `toWireContext`
 *      returns `undefined` for anything outside {@link ScreenContext}, and the
 *      caller omits the `context` key from the body entirely. This is not
 *      politeness: `contextSchema` on the server is `.strict()` and
 *      `selectedEntity.kind` is a closed `z.enum`, so a kind the server has
 *      never heard of is a 400 for the WHOLE TURN — the person's question dies
 *      because a screen guessed. Dropping is the only behaviour that keeps a
 *      newer client from breaking against an older server.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY `route` IS A SERVER-VOCABULARY LITERAL AND NOT `window.location`.
 *
 * The obvious implementation sends the browser path. It is the wrong one twice
 * over. First, the server's `ROUTE_MAP` keys on its own prefixes (`workers/`,
 * `schedule`, `mileage`, `dashboard`), so this app's `/people/$workerId` falls
 * through to the catch-all and the hint is lost exactly where it is worth
 * most. Second, a browser path is an open string: `clients/Avery-Chandler` is
 * a perfectly ordinary URL and a client's name on the wire, and `route` is one
 * of the two documented residuals the X8 provider-boundary gate cannot mask
 * (`providerBoundContext` passes it verbatim). So `route` here is chosen BY
 * THE KIND from {@link SCREEN_ROUTES} — five fixed literals, no interpolation,
 * nothing derived from the address bar.
 */

import {
  ANALYTICS_PRESETS,
  ANALYTICS_VIEWS,
  normalizePreset,
  normalizeView,
  type AnalyticsPreset,
  type AnalyticsView,
} from '../../dashboard/analytics/analyticsRange.js';

/**
 * The screens this app can describe to the assistant. Deliberately five: each
 * is a screen where a question about what is on it is a question the runtime
 * has a tool for. Adding a kind means adding a route literal, a validator for
 * whatever identifier it carries, and — before it is worth anything — a
 * starter in `screenSuggestions.ts` that an advertised tool can answer.
 */
export type ScreenContext =
  | { readonly kind: 'worker'; readonly id: string }
  | { readonly kind: 'schedule'; readonly date?: string }
  | {
      readonly kind: 'dashboard';
      readonly layoutId?: string;
      /** The analytics workspace view (`/dashboard?view=`), normalized as it renders. */
      readonly view?: AnalyticsView;
      /** The period the workspace shows; `custom` only with `from`/`to`. */
      readonly period?: AnalyticsPreset;
      readonly from?: string;
      readonly to?: string;
      /** The workspace's program / site filters (`?program=` / `?site=`), when set. */
      readonly programId?: string;
      readonly siteId?: string;
    }
  | { readonly kind: 'automation'; readonly ruleId?: string }
  | { readonly kind: 'mileage'; readonly period?: string };

export type ScreenKind = ScreenContext['kind'];

function isAnalyticsView(value: string): value is AnalyticsView {
  return ANALYTICS_VIEWS.some((view) => view.value === value);
}

function isAnalyticsPreset(value: string): value is AnalyticsPreset {
  return ANALYTICS_PRESETS.some((preset) => preset.value === value);
}

/**
 * The `context` field of `POST /api/intelligence/turn`, narrowed to the subset
 * this app sends. The server's contract (`contextSchema`) also accepts
 * `scope`, `roles` and free-form `filters`; this type omits `scope` and
 * `roles` because nothing here has an honest value for them, and constrains
 * `filters` to the identifier pairs the mapping below writes.
 *
 * `selectedEntity.kind` is narrowed to the two members of the server's closed
 * `contextEntityKindSchema` enum this app can honestly select. The compile-time
 * narrowing is not the guarantee — `intelligence-global-context` reads the
 * server file and pins the subset relationship, so a server-side rename fails
 * a test here rather than a turn in production.
 */
export interface WireScreenContext {
  readonly route: string;
  readonly selectedEntity?: { readonly kind: 'worker' | 'schedule'; readonly id?: string };
  readonly filters?: Readonly<Record<string, string>>;
  readonly timeRange?: { readonly from: string; readonly to: string };
}

/**
 * Kind → the server's own route vocabulary. Fixed literals, one per kind. See
 * the header for why these are not the browser's paths.
 *
 * All five now resolve to a mapping of their own on the server. `automations`
 * used to be the exception — `ROUTE_MAP` had no automation prefix, so an
 * automation screen fell through to the organization catch-all and got a
 * strictly weaker hint than the other four. F225 added the prefix (its mapping
 * names the registered `automation` tool family), so the literal below is no
 * longer a knowing shortfall.
 *
 * These strings are the SERVER'S vocabulary, not this app's paths, and
 * `intelligence-global-context` reads `context.ts` and asserts a `ROUTE_MAP`
 * prefix exists for every one of them. Renaming either side fails that test
 * rather than silently degrading a live hint to the catch-all.
 */
export const SCREEN_ROUTES: Readonly<Record<ScreenKind, string>> = {
  worker: 'workers/',
  schedule: 'schedule',
  dashboard: 'dashboard',
  automation: 'automations',
  mileage: 'mileage',
};

/**
 * The periods a mileage screen may name. A closed list, not a shape test: a
 * token-shaped free value passes the server's boundary gate verbatim, and a
 * single-word name is indistinguishable from a status token (the documented
 * lane-22 residual). Closing the list closes that door on this side of it.
 */
export const MILEAGE_PERIODS = [
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
] as const;

export type MileagePeriod = (typeof MILEAGE_PERIODS)[number];

/**
 * UUID shape, mirroring what the server accepts. `z.string().uuid()` in zod 3
 * is hex-lenient (it does not require the v4 version/variant nibbles), and the
 * X8 boundary gate's own `FILTER_UUID_SHAPE` is this exact pattern. Matching
 * rather than tightening it means a legitimate non-v4 identifier is never
 * silently dropped, while nothing name-shaped can pass: a name needs a letter
 * outside `[a-f]`, a space, or the wrong length.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Calendar date, no time and no zone — the only date shape that crosses. */
const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

function isMileagePeriod(value: string): value is MileagePeriod {
  return (MILEAGE_PERIODS as readonly string[]).includes(value);
}

/** One URL search value, if it is present AND a string. Never coerces. */
function searchString(search: unknown, key: string): string | undefined {
  if (typeof search !== 'object' || search === null) return undefined;
  const value = (search as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * The typed screen for a route, or `undefined` when this app has nothing
 * honest to say about where the person is.
 *
 * `undefined` is the common case and the right one: most screens carry no
 * identifier the assistant can use, and a context saying only "they are
 * somewhere in the admin app" buys nothing and costs a field on the wire.
 *
 * The two failure modes are deliberately different, because the two screens
 * are. A worker screen IS its worker: an id that is not UUID-shaped means the
 * route did not match what this function thought it matched, so the whole
 * context is dropped. A schedule, dashboard, automation or mileage screen is
 * still that screen without its optional identifier, so a malformed search
 * parameter degrades to the bare kind rather than discarding a hint the
 * pathname alone already earned.
 */
export function screenContextFor(pathname: string, search?: unknown): ScreenContext | undefined {
  const path = pathname.replace(/\/+$/, '');

  const worker = /^\/people\/([^/]+)$/.exec(path);
  if (worker !== null) {
    const id = worker[1] ?? '';
    return UUID_SHAPE.test(id) ? { kind: 'worker', id } : undefined;
  }

  if (path === '/scheduling') {
    const date = searchString(search, 'date');
    return date !== undefined && ISO_DATE_SHAPE.test(date)
      ? { kind: 'schedule', date }
      : { kind: 'schedule' };
  }

  if (path === '/dashboards') {
    const layoutId = searchString(search, 'layout');
    return layoutId !== undefined && UUID_SHAPE.test(layoutId)
      ? { kind: 'dashboard', layoutId }
      : { kind: 'dashboard' };
  }

  // THE ANALYTICS WORKSPACE: the view and period it is SHOWING, normalized by
  // the same functions `AnalyticsProvider` renders from (so an absent `?view=`
  // is Pulse and an absent `?preset=` is this week — what is on screen). The
  // mileage `period` pattern, with a custom range as two ISO dates. A
  // from-only range ends today, as the workspace's own API resolves it
  // (ponytail: the browser's today, which is the org's except across a zone
  // boundary at midnight). Program / site ride along so a filtered screen is
  // not explained org-wide.
  if (path === '/dashboard') {
    const period = normalizePreset(searchString(search, 'preset'));
    const from = searchString(search, 'from');
    const to = searchString(search, 'to') ?? new Date().toLocaleDateString('en-CA');
    const programId = searchString(search, 'program');
    const siteId = searchString(search, 'site');
    return {
      kind: 'dashboard',
      view: normalizeView(searchString(search, 'view')),
      period,
      ...(period === 'custom' && from !== undefined ? { from, to } : {}),
      ...(programId ? { programId } : {}),
      ...(siteId ? { siteId } : {}),
    };
  }

  if (path === '/settings/rules') {
    const ruleId = searchString(search, 'rule');
    return ruleId !== undefined && UUID_SHAPE.test(ruleId)
      ? { kind: 'automation', ruleId }
      : { kind: 'automation' };
  }

  if (path === '/mileage') {
    const period = searchString(search, 'period');
    return period !== undefined && isMileagePeriod(period)
      ? { kind: 'mileage', period }
      : { kind: 'mileage' };
  }

  return undefined;
}

/**
 * The value that goes on the wire, or `undefined` for "send no `context` key
 * at all".
 *
 * Every branch WRITES its fields; none copies an object through. That is the
 * property worth protecting: a spread of the caller's value would let a field
 * this module has never heard of ride along, and the whole point of the closed
 * union is that there is no such field. Re-validating identifiers here rather
 * than trusting {@link screenContextFor} is deliberate too — this function is
 * the boundary, and a `ScreenContext` can be constructed by any caller.
 */
export function toWireContext(screen: ScreenContext | undefined): WireScreenContext | undefined {
  // `typeof null === 'object'`, so null needs its own arm — this is the guard
  // and it has to be total over what an untyped caller can hand it.
  if (screen === undefined || screen === null || typeof screen !== 'object') return undefined;

  switch (screen.kind) {
    case 'worker': {
      const id: unknown = screen.id;
      if (typeof id !== 'string' || !UUID_SHAPE.test(id)) return undefined;
      return { route: SCREEN_ROUTES.worker, selectedEntity: { kind: 'worker', id } };
    }
    case 'schedule': {
      const date: unknown = screen.date;
      const dated = typeof date === 'string' && ISO_DATE_SHAPE.test(date);
      return {
        route: SCREEN_ROUTES.schedule,
        selectedEntity: { kind: 'schedule' },
        ...(dated ? { timeRange: { from: date as string, to: date as string } } : {}),
      };
    }
    case 'dashboard': {
      const layoutId: unknown = screen.layoutId;
      const view: unknown = screen.view;
      const period: unknown = screen.period;
      const from: unknown = screen.from;
      const to: unknown = screen.to;
      const programId: unknown = screen.programId;
      const siteId: unknown = screen.siteId;
      const ranged =
        typeof from === 'string' &&
        typeof to === 'string' &&
        ISO_DATE_SHAPE.test(from) &&
        ISO_DATE_SHAPE.test(to) &&
        from <= to;
      const filters = {
        ...(typeof layoutId === 'string' && UUID_SHAPE.test(layoutId) ? { layoutId } : {}),
        ...(typeof view === 'string' && isAnalyticsView(view) ? { view } : {}),
        ...(typeof period === 'string' &&
        isAnalyticsPreset(period) &&
        (period !== 'custom' || ranged)
          ? { period }
          : {}),
        ...(typeof programId === 'string' && UUID_SHAPE.test(programId) ? { programId } : {}),
        ...(typeof siteId === 'string' && UUID_SHAPE.test(siteId) ? { siteId } : {}),
      };
      return {
        route: SCREEN_ROUTES.dashboard,
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
        ...(ranged && period === 'custom'
          ? { timeRange: { from: from as string, to: to as string } }
          : {}),
      };
    }
    case 'automation': {
      const ruleId: unknown = screen.ruleId;
      const known = typeof ruleId === 'string' && UUID_SHAPE.test(ruleId);
      return {
        route: SCREEN_ROUTES.automation,
        ...(known ? { filters: { ruleId: ruleId as string } } : {}),
      };
    }
    case 'mileage': {
      const period: unknown = screen.period;
      const known = typeof period === 'string' && isMileagePeriod(period);
      return {
        route: SCREEN_ROUTES.mileage,
        ...(known ? { filters: { period: period as string } } : {}),
      };
    }
    default:
      // An unrecognized kind — a screen this build does not know, a value read
      // off something it should not have been read off, or a future kind from
      // a newer bundle. It is DROPPED. Forwarding it would 400 the whole turn
      // against the server's strict, closed contract.
      return undefined;
  }
}

/**
 * What the panel tells a person about the context it carries, in words. By
 * KIND only: naming the worker would mean reading a name the assistant has not
 * been given, on a surface whose entire claim is that it does not do that.
 */
export function screenContextNote(screen: ScreenContext | undefined): string | undefined {
  if (screen === undefined) return undefined;
  switch (screen.kind) {
    case 'worker':
      return 'Questions here can be about the worker on this screen.';
    case 'schedule':
      return 'Questions here can be about the schedule on this screen.';
    case 'dashboard':
      return screen.period !== undefined
        ? 'Questions here can be about this dashboard and the period it shows.'
        : 'Questions here can be about this dashboard.';
    case 'automation':
      return 'Questions here can be about your automation rules.';
    case 'mileage':
      return 'Questions here can be about mileage.';
    default:
      return undefined;
  }
}
